import { Listbox } from '@headlessui/react';
import type { Dispatch, MouseEvent, SetStateAction } from 'react';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/sz/arrow-down.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/sz/trash-icon.svg';
import { useTranslate } from 'hooks';
import { twMergeClsx } from 'utils/tw-with-clsx';
import { validateCard, validateCardType } from 'utils/validate-card';

export const renderIcon = (cardNumber: string) => {
  const CardBank = validateCard(cardNumber);

  if (CardBank) {
    return <div>{CardBank}</div>;
  }

  const CardType = validateCardType(cardNumber);

  if (CardType) {
    return <CardType />;
  }

  return <div />;
};

type CardOptionType = {
  crypt?: string;
  maskedPan: string;
  bankBin?: string;
  newCard?: boolean;
  id: number;
};

const SelectCard = ({
  selectedCard,
  cards,
  handleDelete,
  setSelectedCard,
}: {
  cards: CardOptionType[];
  selectedCard: CardOptionType;
  handleDelete: (value: string) => void;
  setSelectedCard: Dispatch<SetStateAction<CardOptionType>>;
}) => {
  const { t } = useTranslate();
  const defaulValue = { maskedPan: t('1xbet.new-bank-card'), newCard: true };

  const renderBtnContent = () => {
    if (selectedCard) {
      if (selectedCard.newCard) {
        return <span className="flex-grow">{selectedCard.maskedPan}</span>;
      }

      return selectedCard.bankBin ? (
        <>
          {renderIcon(selectedCard.bankBin)}
          <span className="flex-grow">{selectedCard.maskedPan}</span>
        </>
      ) : null;
    }

    return null;
  };

  const deleteCard = (e: MouseEvent<HTMLButtonElement>, pan: string) => {
    handleDelete(pan);
    e.stopPropagation();
  };

  return (
    <Listbox
      as="div"
      className="relative mt-3 md:mt-[30px] text-sm md:text-base"
      value={selectedCard}
      onChange={setSelectedCard}
    >
      {({ open }) => (
        <>
          <Listbox.Button className="z-10 relative px-3 bg-base-gray-700 rounded-xsmall h-10 md:h-[38px] text-left gap-[10px] flex items-center transition-colors md:px-1 bg-[transparent] md:rounded-none md:border-b border-solid w-full border-base-gray-500 outline-none text-sm md:text-base leading-5 md:pb-[10px] hover:border-base-dark-gray-200 disabled:border-base-gray-500 focus:border-base-dark-gray-200 placeholder:text-base-gray-600/50">
            {renderBtnContent()}
            <ArrowDownIcon
              className={twMergeClsx(
                'w-5 h-5 transition-transform fill-1xbet-black',
                open && 'rotate-180',
              )}
            />
          </Listbox.Button>
          <Listbox.Options className="absolute px-3 py-[10px] md:py-3 md:px-4 w-full z-50 rounded-[10px] mt-[6px] md:mt-0 bg-base-gray-700 md:bg-white overflow-hidden">
            <Listbox.Option
              className="pb-[10px] last:pb-0 mt-4 first:mt-0 border-b border-b-base-gray-500 hover:border-base-dark-gray-200 last:border-none cursor-pointer transition-colors"
              value={defaulValue}
            >
              {defaulValue.maskedPan}
            </Listbox.Option>
            {cards?.map((item) => (
              <Listbox.Option
                className="pb-[10px] mt-[10px] last:pb-0 first:mt-0 border-b border-b-sz-gray-600 last:border-none cursor-pointer transition-colors flex items-center gap-[10px]"
                value={item}
                key={item.maskedPan}
              >
                {item.bankBin && renderIcon(item.bankBin)}
                <span className="flex-grow">{item.maskedPan}</span>
                <button
                  className="w-6 h-6"
                  type="button"
                  onClick={(e) => deleteCard(e, item.maskedPan)}
                >
                  <TrashIcon className="fill-1xbet-black" />
                </button>
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </>
      )}
    </Listbox>
  );
};

export { SelectCard };
